import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Autocomplete, Alert, Chip, CircularProgress, Snackbar, Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import axios from 'axios';
import useSWR from 'swr';
import { useSelector } from 'react-redux';
import { apiHost } from '../utils/apiHost';
import { waktuCabang } from '../utils/waktuCabang';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};
const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);
const rp = (n) => `Rp ${Math.round(Number(n) || 0).toLocaleString('id-ID')}`;
const ribuan = (v) => (v ? Number(String(v).replace(/\D/g, '')).toLocaleString('id-ID') : '');
const hariIniCabang = () => {
  const d = waktuCabang(new Date());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const tglIndo = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });

const FORM_KOSONG = { tanggal: '', kategori: '', nominal: '', keterangan: '', cabanguuid: '' };

/** Catatan biaya operasional cabang; dipakai dashboard untuk menghitung laba bersih. */
export const Pengeluaran = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [bulan, setBulan] = useState(() => hariIniCabang().slice(0, 7));
  const [cabang, setCabang] = useState('');
  const [form, setForm] = useState(null); // null = dialog tertutup
  const [ubah, setUbah] = useState(null); // uuid yang diubah
  const [menyimpan, setMenyimpan] = useState(false);
  const [galatForm, setGalatForm] = useState('');
  const [pesan, setPesan] = useState('');

  const url = user ? `${getApiBaseUrl()}/pengeluaran?bulan=${bulan}${superadmin && cabang ? `&cabangUuid=${cabang}` : ''}` : null;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher, { keepPreviousData: true });
  const { data: cabangList } = useSWR(superadmin ? `${getApiBaseUrl()}/cabang` : null, fetcher);
  const daftar = data?.data || [];

  const buka = (item) => {
    setGalatForm('');
    setUbah(item?.uuid || null);
    setForm(item
      ? { tanggal: item.tanggal, kategori: item.kategori, nominal: String(item.nominal), keterangan: item.keterangan || '', cabanguuid: item.cabanguuid }
      : { ...FORM_KOSONG, tanggal: hariIniCabang(), cabanguuid: superadmin ? cabang : '' });
  };

  const simpan = async () => {
    if (!form.tanggal || !form.kategori.trim() || !Number(String(form.nominal).replace(/\D/g, ''))) {
      setGalatForm('Tanggal, kategori, dan nominal harus diisi.');
      return;
    }
    if (superadmin && !form.cabanguuid) {
      setGalatForm('Pilih cabang.');
      return;
    }
    setMenyimpan(true);
    setGalatForm('');
    try {
      const body = {
        tanggal: form.tanggal,
        kategori: form.kategori.trim(),
        nominal: Number(String(form.nominal).replace(/\D/g, '')),
        keterangan: form.keterangan,
        ...(superadmin ? { cabanguuid: form.cabanguuid } : {}),
      };
      if (ubah) await axios.put(`${getApiBaseUrl()}/pengeluaran/${ubah}`, body, { withCredentials: true });
      else await axios.post(`${getApiBaseUrl()}/pengeluaran`, body, { withCredentials: true });
      setForm(null);
      setPesan(ubah ? 'Pengeluaran diperbarui' : 'Pengeluaran dicatat');
      mutate();
    } catch (e) {
      setGalatForm(e.response?.data?.message || e.message);
    } finally {
      setMenyimpan(false);
    }
  };

  const hapus = async (item) => {
    const ok = await Swal.fire({
      title: 'Hapus pengeluaran?',
      text: `${item.kategori} · ${rp(item.nominal)} (${tglIndo(item.tanggal)})`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d33',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${getApiBaseUrl()}/pengeluaran/${item.uuid}`, { withCredentials: true });
      setPesan('Pengeluaran dihapus');
      mutate();
    } catch (e) {
      Swal.fire('Gagal', e.response?.data?.message || e.message, 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pengeluaran</Typography>
          <Typography variant="body2" color="text.secondary">
            Biaya operasional cabang (sewa, listrik, gas, dll.) untuk menghitung laba bersih di dashboard.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Bulan" type="month" size="small" value={bulan}
            onChange={(e) => e.target.value && setBulan(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: 170 }}
          />
          {superadmin && (
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
              <InputLabel shrink>Cabang</InputLabel>
              <Select
                value={cabang} label="Cabang" notched displayEmpty
                renderValue={(v) => (v ? cabangList?.data?.find((c) => c.uuid === v)?.namacabang || '-' : 'Semua cabang')}
                onChange={(e) => setCabang(e.target.value)}
              >
                <MenuItem value="">Semua cabang</MenuItem>
                {cabangList?.data?.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => buka(null)}>Tambah</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.response?.data?.message || error.message}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total pengeluaran</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#E53935' }}>{rp(data?.total)}</Typography>
              <Typography variant="caption" color="text.secondary">{daftar.length} catatan</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Per kategori</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(data?.perKategori || []).map((k) => <Chip key={k.kategori} label={`${k.kategori}: ${rp(k.nominal)}`} />)}
                {!data?.perKategori?.length && <Typography variant="body2" color="text.disabled">Belum ada pengeluaran.</Typography>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tanggal</TableCell>
                {superadmin && <TableCell>Cabang</TableCell>}
                <TableCell>Kategori</TableCell>
                <TableCell>Keterangan</TableCell>
                <TableCell align="right">Nominal</TableCell>
                <TableCell>Dicatat oleh</TableCell>
                <TableCell align="right">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && !data && (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {data && !daftar.length && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.disabled', py: 4 }}>Belum ada pengeluaran di bulan ini.</TableCell></TableRow>
              )}
              {daftar.map((p) => (
                <TableRow key={p.uuid} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{tglIndo(p.tanggal)}</TableCell>
                  {superadmin && <TableCell>{p.namaCabang}</TableCell>}
                  <TableCell>{p.kategori}</TableCell>
                  <TableCell>{p.keterangan || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rp(p.nominal)}</TableCell>
                  <TableCell>{p.dicatatOleh || '-'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Ubah"><IconButton size="small" onClick={() => buka(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Hapus"><IconButton size="small" color="error" onClick={() => hapus(p)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!form} onClose={() => !menyimpan && setForm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{ubah ? 'Ubah pengeluaran' : 'Tambah pengeluaran'}</DialogTitle>
        {form && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            {galatForm && <Alert severity="error">{galatForm}</Alert>}
            {superadmin && (
              <FormControl fullWidth size="small">
                <InputLabel>Cabang *</InputLabel>
                <Select value={form.cabanguuid} label="Cabang *" onChange={(e) => setForm({ ...form, cabanguuid: e.target.value })}>
                  {cabangList?.data?.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Tanggal *" type="date" size="small" value={form.tanggal}
              onChange={(e) => setForm({ ...form, tanggal: e.target.value })} InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              freeSolo options={data?.kategori || []} value={form.kategori}
              onInputChange={(_, v) => setForm((f) => ({ ...f, kategori: v }))}
              renderInput={(params) => <TextField {...params} label="Kategori *" size="small" inputProps={{ ...params.inputProps, maxLength: 50 }} />}
            />
            <TextField
              label="Nominal *" size="small" value={ribuan(form.nominal)} inputMode="numeric"
              onChange={(e) => setForm({ ...form, nominal: e.target.value.replace(/\D/g, '') })}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>Rp</Typography> }}
            />
            <TextField
              label="Keterangan" size="small" multiline minRows={2} value={form.keterangan}
              onChange={(e) => setForm({ ...form, keterangan: e.target.value })} inputProps={{ maxLength: 255 }}
            />
            <Typography variant="caption" color="text.secondary">
              Belanja stok yang sudah dihitung lewat harga modal produk tidak perlu dicatat lagi di sini, supaya tidak terhitung dua kali.
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={menyimpan}>Batal</Button>
          <Button variant="contained" onClick={simpan} disabled={menyimpan}>{menyimpan ? 'Menyimpan…' : 'Simpan'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!pesan} autoHideDuration={2500} onClose={() => setPesan('')} message={pesan} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};
